import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
  Popover,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Slider,
  Divider,
  Grid,
  Stack,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search,
  SwapHoriz,
  KeyboardArrowDown,
  Add,
  Remove,
} from "@mui/icons-material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { useNavigate } from "react-router-dom";
import cities from "../data/cities.json";
import DatePickerPopover from "./DatePickerPopover";
import PassengerCounter from "./PassengerCounter";
import FlightSegment from "./FlightSegment";
import PassengerMenu from "./PassengerMenu";
import PassengerDropdown, { getTravelerLabel } from "./PassengerDropdown";
import {
  TRIP_TYPES,
  DEFAULT_INITIAL_VALUES,
} from "../constants/flightSearchConstants";
import {
  formatDate,
  generateCalendarDates,
  generatePriceForDate,
  getPriceColor,
  getPriceBackgroundColor,
  getPassengerDisplayText,
} from "../utils/flightSearchUtils";
import Popper from "@mui/material/Popper";
import { useSearchContext } from "../context/SearchContext";

// Custom Popper for wider dropdown
function CustomPopper(props) {
  return (
    <Popper
      {...props}
      style={{ width: 520, zIndex: 1300 }}
      placement="bottom-start"
    />
  );
}

function FlightSearchBar({
  initialFrom = DEFAULT_INITIAL_VALUES.initialFrom,
  initialTo = DEFAULT_INITIAL_VALUES.initialTo,
  initialDepartureDate = DEFAULT_INITIAL_VALUES.initialDepartureDate,
  initialReturnDate = DEFAULT_INITIAL_VALUES.initialReturnDate,
  initialAdults = DEFAULT_INITIAL_VALUES.initialAdults,
  initialStudents = DEFAULT_INITIAL_VALUES.initialStudents,
  initialSeniors = DEFAULT_INITIAL_VALUES.initialSeniors,
  initialYouths = DEFAULT_INITIAL_VALUES.initialYouths,
  initialChildren = DEFAULT_INITIAL_VALUES.initialChildren,
  initialToddlers = DEFAULT_INITIAL_VALUES.initialToddlers,
  initialInfants = DEFAULT_INITIAL_VALUES.initialInfants,
  initialTripType = DEFAULT_INITIAL_VALUES.initialTripType,
  initialCabin = DEFAULT_INITIAL_VALUES.initialCabin,
  initialBags = DEFAULT_INITIAL_VALUES.initialBags,
  onSubmit,
  onFlightSegmentsChange,
  onTripTypeChange,
  buttonLabel = DEFAULT_INITIAL_VALUES.buttonLabel,
}) {
  const navigate = useNavigate();
  const {
    from,
    setFrom,
    to,
    setTo,
    departureDate,
    setDepartureDate,
    returnDate,
    setReturnDate,
    flightSegments,
    setFlightSegments,
    adults,
    setAdults,
    students,
    setStudents,
    seniors,
    setSeniors,
    youths,
    setYouths,
    childrenCount,
    setChildrenCount,
    toddlers,
    setToddlers,
    infants,
    setInfants,
    carryOnBags,
    setCarryOnBags,
    checkedBags,
    setCheckedBags,
    tripType,
    setTripType,
    cabin,
    setCabin,
    directOnly,
    setDirectOnly,
    compareKayak,
    setCompareKayak,
    includeSouthwest,
    setIncludeSouthwest,
    weekendSelectedDate,
    setWeekendSelectedDate,
    weekendDeparture,
    setWeekendDeparture,
    weekendReturn,
    setWeekendReturn,
    isWeekendMode,
    setIsWeekendMode,
    monthDuration,
    setMonthDuration,
    isMonthMode,
    setIsMonthMode,
    departureExact,
    setDepartureExact,
    returnExact,
    setReturnExact,
    activeDateField,
    setActiveDateField,
    datePopoverAnchor,
    setDatePopoverAnchor,
    activeSegmentId,
    setActiveSegmentId,
    currentMonth,
    setCurrentMonth,
    dateTab,
    setDateTab,
    searchBarRef,
    passengerButtonRef,
    fromFieldOpen,
    setFromFieldOpen,
    toFieldOpen,
    setToFieldOpen,
  } = useSearchContext();

  // Local state for dropdowns and menus
  const [passengersAnchorEl, setPassengersAnchorEl] = useState(null);
  const [tripTypeAnchorEl, setTripTypeAnchorEl] = useState(null);
  const [bagsAnchorEl, setBagsAnchorEl] = useState(null);
  const [toQuery, setToQuery] = useState("");
  const defaultFilter = (options, params) => {
    const input = (params?.inputValue || toQuery || "").toLowerCase();
    if (!input) return options.slice(0, 200);
    return options
      .filter((o) =>
        [
          o.displayname,
          o.name,
          o.city,
          o.region,
          o.country,
          o.code,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(input))
      )
      .slice(0, 200);
  };

  // Initialize flight segments count on component mount
  useEffect(() => {
    if (onFlightSegmentsChange) {
      onFlightSegmentsChange(flightSegments);
    }
    closeAllDropdowns();
  }, []);

  // Reset passenger counts when trip type changes
  useEffect(() => {
    setAdults(1);
    setStudents(0);
    setSeniors(0);
    setYouths(0);
    setChildrenCount(0);
    setToddlers(0);
    setInfants(0);
  }, [tripType]);

  // Helper function to group and sort cities for autocomplete
  const getGroupedCities = () => {
    // Group cities by metro area
    const metroGroups = {};
    const singleCities = [];

    cities.forEach((city) => {
      if (city.isMetroOnly) {
        metroGroups[city.code] = {
          metro: city,
          airports: [],
        };
      } else if (city.sub && city.parentMetro) {
        if (!metroGroups[city.parentMetro]) {
          metroGroups[city.parentMetro] = {
            metro: null,
            airports: [],
          };
        }
        metroGroups[city.parentMetro].airports.push(city);
      } else {
        singleCities.push(city);
      }
    });

    // Create final grouped array
    const result = [];

    // Add metro groups (main city first, then airports)
    Object.values(metroGroups).forEach((group) => {
      if (group.metro) {
        result.push(group.metro);
        group.airports.forEach((airport) => {
          result.push(airport);
        });
      }
    });

    // Add single cities
    singleCities.forEach((city) => {
      result.push(city);
    });

    return result;
  };

  const groupedCities = getGroupedCities();

  // Ensure dropdown opens at the top rather than auto-scrolling to a selected item
  const resetListboxScrollToTop = (listboxId) => {
    // Wait for popper to render
    setTimeout(() => {
      const list = document.getElementById(listboxId);
      if (list) {
        list.scrollTop = 0;
      }
    }, 50);
  };

  // Helper functions for passenger display
  const getPassengerDisplay = () => {
    return getPassengerDisplayText(
      adults,
      students,
      seniors,
      youths,
      childrenCount,
      toddlers,
      infants
    );
  };

  // Helper function to format date with flexibility
  const formatDateWithFlexibility = (
    date,
    exactType,
    isWeekend = false,
    weekendDeparture = null,
    weekendReturn = null
  ) => {
    if (!date && !isWeekend) return null;

    if (isWeekend && date) {
      // Weekend mode with selected date
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const d = new Date(date);
      const dayOfWeek = dayNames[d.getDay()];
      const month = d.getMonth() + 1;
      const day = d.getDate();

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
            {`${dayOfWeek} ${month}/${day}`}
          </Typography>
          <Typography
            sx={{ fontSize: "10px", color: "#666", lineHeight: "14px" }}
          >
            +4 weeks
          </Typography>
        </Box>
      );
    }

    if (!date) return null;

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const d = new Date(date);
    const dayOfWeek = dayNames[d.getDay()];
    const month = d.getMonth() + 1;
    const day = d.getDate();

    const formattedDate = `${dayOfWeek} ${month}/${day}`;

    if (exactType === "exact") {
      return formattedDate;
    }

    const flexibilityText = {
      day_after: "+ day after",
      day_before: "+ day before",
      plus_minus_1: "± 1 day",
      plus_minus_2: "± 2 days",
      plus_minus_3: "± 3 days",
    }[exactType];

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
          {formattedDate}
        </Typography>
        <Typography
          sx={{ fontSize: "10px", color: "#666", lineHeight: "14px" }}
        >
          {flexibilityText}
        </Typography>
      </Box>
    );
  };

  // Helper for bags label
  const getBagsLabel = () => {
    if (carryOnBags === 1 && checkedBags === 0) return "1 carry-on bag";
    if (carryOnBags === 0 && checkedBags === 1) return "1 checked bag";
    if (carryOnBags === 0 && checkedBags === 2) return "2 checked bags";
    if (carryOnBags === 1 && checkedBags === 1) return "2 bags";
    if (carryOnBags === 1 && checkedBags === 2) return "3 bags";
    if (carryOnBags === 0 && checkedBags === 0) return "0 bags";
    return `${carryOnBags + checkedBags} bags`;
  };

  // Helper to format weekend option labels
  const formatWeekendLabel = (value) => {
    const labelMap = {
      thursday: "Thu",
      thu_am: "Thu AM",
      thu_pm: "Thu PM",
      friday: "Fri",
      fri_am: "Fri AM",
      fri_pm: "Fri PM",
      saturday: "Sat",
      sat_am: "Sat AM",
      sat_pm: "Sat PM",
      sunday: "Sun",
      sun_am: "Sun AM",
      sun_pm: "Sun PM",
      monday: "Mon",
      mon_am: "Mon AM",
      mon_pm: "Mon PM",
    };
    return labelMap[value] || value;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      const submitData =
        tripType === "Multi-city"
          ? {
            flightSegments,
            adults,
            students,
            seniors,
            youths,
            childrenCount,
            toddlers,
            infants,
            bags: carryOnBags + checkedBags,
            tripType,
            cabin,
            directOnly,
            compareKayak,
            includeSouthwest,
          }
          : {
            from,
            to,
            departureDate,
            returnDate,
            adults,
            students,
            seniors,
            youths,
            childrenCount,
            toddlers,
            infants,
            bags: carryOnBags + checkedBags,
            tripType,
            cabin,
            directOnly,
            compareKayak,
            includeSouthwest,
          };

      onSubmit(submitData);
    }
  };

  const swapLocations = () => {
    const temp = from;
    setFrom(to);
    setTo(temp);
  };

  const clearLocation = (field) => {
    if (field === "from") {
      setFrom([]);
    } else {
      setTo([]);
    }
  };

  // Function to close date picker
  const closeDatePicker = () => {
    setDatePopoverAnchor(null);
    setActiveSegmentId(null);
    setActiveDateField(null);
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setFromFieldOpen(false);
    setToFieldOpen(false);
    setDatePopoverAnchor(null);
    setActiveSegmentId(null);
    setActiveDateField(null);
    setPassengersAnchorEl(null);
    setBagsAnchorEl(null);
    setTripTypeAnchorEl(null);
  };

  // Multi-city segment management
  const addFlightSegment = () => {
    // Only allow adding up to 6 segments
    if (flightSegments.length >= 6) return;

    const newSegment = {
      id: Date.now(),
      from: "",
      to: "",
      date: "",
      cabin: cabin,
    };
    const updatedSegments = [...flightSegments, newSegment];
    setFlightSegments(updatedSegments);
    if (onFlightSegmentsChange) {
      onFlightSegmentsChange(updatedSegments);
    }
  };

  const removeFlightSegment = (segmentId) => {
    if (flightSegments.length > 1) {
      const updatedSegments = flightSegments.filter(
        (segment) => segment.id !== segmentId
      );
      setFlightSegments(updatedSegments);
      if (onFlightSegmentsChange) {
        onFlightSegmentsChange(updatedSegments);
      }
    }
  };

  const updateFlightSegment = (segmentId, field, value) => {
    setFlightSegments((prevSegments) => {
      const updatedSegments = prevSegments.map((segment, idx) => {
        if (segment.id === segmentId) {
          return { ...segment, [field]: value };
        }
        return segment;
      });
      // Auto-fill next two segments' date if setting first segment's date in Multi-city
      if (tripType === "Multi-city" && field === "date") {
        const idx = prevSegments.findIndex((seg) => seg.id === segmentId);
        if (idx === 0) {
          // For index 0 and 1 (second and third segments)
          for (let i = 0; i < 2; i++) {
            if (updatedSegments[i] && !updatedSegments[i].date) {
              updatedSegments[i] = { ...updatedSegments[i], date: value };
            }
          }
        }
      }
      // Auto-fill next segment's 'from' if updating 'to' in Multi-city
      if (tripType === "Multi-city" && field === "to") {
        const idx = prevSegments.findIndex((seg) => seg.id === segmentId);
        if (idx !== -1 && idx < updatedSegments.length - 1) {
          const nextSegment = updatedSegments[idx + 1];
          // Only auto-fill if next 'from' is empty
          if (!nextSegment.from) {
            updatedSegments[idx + 1] = { ...nextSegment, from: value };
          }
        }
      }
      return updatedSegments;
    });
  };

  const clearAllSegments = () => {
    // Clear text fields only, don't remove segments
    const clearedSegments = flightSegments.map((segment) => ({
      ...segment,
      from: "",
      to: "",
      date: "",
    }));
    setFlightSegments(clearedSegments);
    if (onFlightSegmentsChange) {
      onFlightSegmentsChange(clearedSegments);
    }
  };

  // Date selection logic for multi-city
  const isDateSelected = (date, currentDateTab = null) => {
    if (tripType === "Multi-city") {
      const d = new Date(date).setHours(0, 0, 0, 0);
      return flightSegments.some((segment) => {
        const segmentDate = new Date(segment.date).setHours(0, 0, 0, 0);
        return d === segmentDate;
      });
    }

    const d = new Date(date).setHours(0, 0, 0, 0);

    // In MONTH mode (dateTab === 2), only highlight the departure date
    if (currentDateTab === 2) {
      const dep = new Date(departureDate).setHours(0, 0, 0, 0);
      return d === dep && departureDate;
    }

    // Check for weekend selected date
    if (weekendSelectedDate) {
      const weekendDate = new Date(weekendSelectedDate).setHours(0, 0, 0, 0);
      if (d === weekendDate) return true;
    }

    const dep = new Date(departureDate).setHours(0, 0, 0, 0);
    const ret = new Date(returnDate).setHours(0, 0, 0, 0);
    if (tripType === "One-way") return d === dep;
    return d === dep || d === ret;
  };

  const isDateInRange = (date, currentDateTab = null) => {
    // In MONTH mode (dateTab === 2), don't show date ranges
    if (currentDateTab === 2) return false;

    if (tripType === "One-way" || tripType === "Multi-city") return false;
    const d = new Date(date).setHours(0, 0, 0, 0);
    const dep = new Date(departureDate).setHours(0, 0, 0, 0);
    const ret = new Date(returnDate).setHours(0, 0, 0, 0);
    return dep && ret && d > dep && d < ret;
  };

  const handleDateSelect = (date) => {
    if (tripType === "Multi-city" && activeSegmentId) {
      updateFlightSegment(activeSegmentId, "date", date);
      setDatePopoverAnchor(null);
      setActiveSegmentId(null);
      // Auto-open passenger menu after multi-city date selection
      setTimeout(() => {
        setPassengersAnchorEl(passengerButtonRef.current);
      }, 300);
    } else if (tripType === "One-way") {
      setDepartureDate(date);
      setDatePopoverAnchor(null);
      setActiveDateField(null);
      // Auto-open passenger menu after one-way date selection
      setTimeout(() => {
        setPassengersAnchorEl(passengerButtonRef.current);
      }, 300);
    } else {
      if (activeDateField === "departure") {
        setDepartureDate(date);
        setReturnDate("");
        setActiveDateField("return");
        // Don't close the popover, switch to return selection
      } else if (activeDateField === "return") {
        if (new Date(date) > new Date(departureDate)) {
          setReturnDate(date);
          setDatePopoverAnchor(null);
          setActiveDateField(null);
          // Auto-open passenger menu after return date selection
          setTimeout(() => {
            setPassengersAnchorEl(passengerButtonRef.current);
          }, 300);
        } else {
          setDepartureDate(date);
          setReturnDate("");
          setActiveDateField("return");
        }
      } else {
        setDepartureDate(date);
        setReturnDate("");
        setActiveDateField("return");
      }
    }
  };

  // Bags dropdown outside click handler
  const bagsDropdownRef = useRef(null);
  useEffect(() => {
    if (!bagsAnchorEl) return;
    const handleClickOutside = (event) => {
      if (
        bagsAnchorEl &&
        !bagsAnchorEl.contains(event.target) &&
        bagsDropdownRef.current &&
        !bagsDropdownRef.current.contains(event.target)
      ) {
        setBagsAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [bagsAnchorEl]);

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      {/* Top Controls Row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        {/* Trip Type Dropdown */}
        <Box>
          <Button
            onClick={(e) => {
              closeAllDropdowns();
              if (tripTypeAnchorEl) {
                setTripTypeAnchorEl(null);
              } else {
                setTripTypeAnchorEl(e.currentTarget);
              }

            }}
            endIcon={<KeyboardArrowDown sx={{ fontSize: "16px", WebkitFontSmoothing: "antialiased", transition: "transform 0.2s ease-in-out", transform: tripTypeAnchorEl ? "rotate(180deg)" : "rotate(0deg)", verticalAlign: "middle", animation: "spin 0.5s linear" }} />}
            sx={{
              color: "#333",
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              minWidth: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
                color: "#000",
              },
            }}
          >
            {tripType}
          </Button>
          <Menu
            anchorEl={tripTypeAnchorEl}
            open={Boolean(tripTypeAnchorEl)}
            onClose={() => setTripTypeAnchorEl(null)}
            onClick={(e) => {
              e.stopPropagation();
              if (tripTypeAnchorEl) {
                setTripTypeAnchorEl(null);
              } else {
                setTripTypeAnchorEl(e.currentTarget);
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                minWidth: "100px",
                mt: 1,
                paddingTop: "0px !important"
              },
            }}

          >
            {TRIP_TYPES.map((type) => (
              <MenuItem
                key={type}
                onClick={() => {
                  setTripType(type);
                  // setTripTypeAnchorEl(null); // Removed as per new_code
                  if (onTripTypeChange) {
                    onTripTypeChange(type);
                  }
                  // Initialize with three segments when switching to multi-city
                  if (type === "Multi-city") {
                    const multiCitySegments = [
                      {
                        id: Date.now(),
                        from: from[0] || "",
                        to: to[0] || "",
                        date: departureDate,
                        cabin: cabin,
                      },
                      {
                        id: Date.now() + 1,
                        from: "",
                        to: "",
                        date: "",
                        cabin: cabin,
                      },
                      {
                        id: Date.now() + 2,
                        from: "",
                        to: "",
                        date: "",
                        cabin: cabin,
                      },
                    ];
                    // Auto-fill second segment's 'to' if first segment's 'from' is set
                    if (multiCitySegments[0].from && !multiCitySegments[1].to) {
                      multiCitySegments[1].to = multiCitySegments[0].from;
                    }
                    setFlightSegments(multiCitySegments);
                    if (onFlightSegmentsChange) {
                      onFlightSegmentsChange(multiCitySegments);
                    }
                  } else {
                    // Reset to single segment when switching from multi-city
                    const resetSegments = [
                      {
                        id: Date.now(),
                        from: from[0] || "",
                        to: to[0] || "",
                        date: departureDate,
                        cabin: cabin,
                      },
                    ];
                    setFlightSegments(resetSegments);
                    if (onFlightSegmentsChange) {
                      onFlightSegmentsChange(resetSegments);
                    }
                  }
                }}
                sx={{
                  fontSize: "14px",
                  backgroundColor:
                    tripType === type ? "#e6ebef" : "transparent",
                  fontWeight: tripType === type ? 600 : 300,
                  "&:hover": {
                    backgroundColor: "#e6ebef",
                  },

                }}
              >
                {type}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Bags Dropdown - show for Round-trip and One-way */}
        {tripType !== "Multi-city" && (
          <Box>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Toggle the bags dropdown
                if (bagsAnchorEl) {
                  setBagsAnchorEl(null);
                } else {
                  closeAllDropdowns();
                  setBagsAnchorEl(e.currentTarget);
                }
              }}
              endIcon={<KeyboardArrowDown sx={{ fontSize: "16px", WebkitFontSmoothing: "antialiased", transition: "transform 0.2s ease-in-out", transform: bagsAnchorEl ? "rotate(180deg)" : "rotate(0deg)", verticalAlign: "middle", animation: "spin 0.5s linear" }} />}
              sx={{
                color: "#333",
                fontWeight: 500,
                fontSize: "14px",
                textTransform: "none",
                minWidth: "auto",
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#000",
                },
                "&:active": {
                  backgroundColor: "transparent !important",
                  color: "#333",
                },
                "&:focus": {
                  backgroundColor: "transparent",
                  color: "#333",
                },
              }}
            >
              {getBagsLabel()}
            </Button>
            {/* Custom Bags Dropdown using Popper */}
            <Popper
              open={Boolean(bagsAnchorEl)}
              anchorEl={bagsAnchorEl}
              placement="bottom-start"
              style={{ zIndex: 1301 }}
              disablePortal={false}
              // modifiers={[
              //   { name: 'offset', options: { offset: [0, 8] } }
              // ]}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  minWidth: 320,
                  maxWidth: 320,
                  maxHeight: 320,
                },
              }}
            >
              <Paper
                ref={bagsDropdownRef}
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 10px 20px rgba(25,32,36,0.1),0 3px 6px rgba(25,32,36,0.04),0 -3px 6px rgba(25,32,36,0.04)",

                  maxWidth: 310,
                  overflow: "hidden",
                  padding: "33px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 1,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Carry-on bag row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="18"
                        height="18"
                        xmlns="http://www.w3.org/2000/svg"
                        role="presentation"
                      >
                        <path d="M126.65 92.48h-53.3a13.29 13.29 0 00-13.3 13.3v53.4a13.29 13.29 0 0013.3 13.3v6.7h13.3v-6.7h26.7v6.7h13.3v-6.7a13.29 13.29 0 0013.3-13.3v-53.3a13.38 13.38 0 00-13.3-13.4zM88.42 34.12a3.33 3.33 0 013.3-3.3H95v3.3h13.3v-3.3h3.3a3.33 3.33 0 013.3 3.3v53.3h10v-53.3a13.29 13.29 0 00-13.3-13.3H91.72a13.29 13.29 0 00-13.3 13.3v53.3h10z"></path>
                      </svg>
                    </Box>
                    <Typography
                      sx={{ fontSize: 15, color: "#222", fontWeight: 500 }}
                    >
                      Carry-on bag
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCarryOnBags(Math.max(0, carryOnBags - 1));
                      }}
                      disabled={carryOnBags === 0}
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: carryOnBags === 0 ? "#ccc" : "#333",
                        backgroundColor: "white",
                        "&:disabled": {
                          color: "#ccc",
                          borderColor: "#eee",
                          backgroundColor: "#f9f9f9",
                        },
                        "&:hover:not(:disabled)": {
                          backgroundColor: "transparent !important",
                          borderColor: "#333",
                        },
                      }}
                    >
                      <Remove sx={{ fontSize: "16px" }} />
                    </IconButton>
                    <TextField
                      type="text"
                      value={carryOnBags}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value < 2)
                          setCarryOnBags(Math.max(0, Math.min(1, value)));
                      }}
                      inputProps={{
                        min: 0,
                        max: 1,
                        style: {
                          textAlign: "center",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#333",
                          padding: "4px",
                          width: "32px",
                          border: "none",
                          outline: "none",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                          "&:hover fieldset": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                        },
                        width: "32px",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCarryOnBags(Math.min(1, carryOnBags + 1));
                      }}
                      disabled={carryOnBags === 1}
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: carryOnBags === 1 ? "#ccc" : "#333",
                        backgroundColor: "white",
                        "&:disabled": {
                          color: "#ccc",
                          borderColor: "#eee",
                          backgroundColor: "#f9f9f9",
                        },
                        "&:hover:not(:disabled)": {
                          backgroundColor: "transparent !important",
                          borderColor: "#333",
                        },
                      }}
                    >
                      <Add sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Box>
                </Box>
                {/* Checked bag row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="18"
                        height="18"
                        xmlns="http://www.w3.org/2000/svg"
                        role="presentation"
                      >
                        <path d="M84.35 40.9h-11.9V25.1a15.83 15.83 0 0115.8-15.8H112a15.83 15.83 0 0115.8 15.8v15.8H116V25.1a3.9 3.9 0 00-3.9-3.9H108v3.9H92.05v-3.9h-3.8a3.9 3.9 0 00-3.9 3.9zm55 7.9h-55.2v17.7L96 78.3v34H68.35v-34l11.8-11.8V48.8h-19.6a15.83 15.83 0 00-15.8 15.8V167a15.83 15.83 0 0015.8 15.8v7.9h15.8v-7.9h47.3v7.3h15.8v-7.3a15.83 15.83 0 0015.8-15.8V64.5a15.9 15.9 0 00-15.9-15.7zm-61.1 27.6a3.9 3.9 0 003.9 3.9a4 4 0 003.9-3.9a3.9 3.9 0 10-7.8 0z"></path>
                      </svg>
                    </Box>
                    <Typography
                      sx={{ fontSize: 15, color: "#222", fontWeight: 500 }}
                    >
                      Checked bag
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCheckedBags(Math.max(0, checkedBags - 1));
                      }}
                      disabled={checkedBags === 0}
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: checkedBags === 0 ? "#ccc" : "#333",
                        backgroundColor: "white",
                        fontWeight: 600,
                        "&:disabled": {
                          color: "#ccc",
                          borderColor: "#eee",
                          backgroundColor: "#f9f9f9",
                        },
                        "&:hover:not(:disabled)": {
                          backgroundColor: "transparent !important",
                          borderColor: "#333",
                        },
                      }}
                    >
                      <Remove sx={{ fontSize: "16px" }} />
                    </IconButton>
                    <TextField
                      type="text"
                      value={checkedBags}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value < 3)
                          setCheckedBags(Math.max(0, Math.min(2, value)));
                      }}
                      inputProps={{
                        min: 0,
                        max: 2,
                        style: {
                          textAlign: "center",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#333",
                          padding: "4px",
                          width: "32px",
                          border: "none",
                          outline: "none",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                        width: "32px",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCheckedBags(Math.min(2, checkedBags + 1));
                      }}
                      disabled={checkedBags === 2}
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: checkedBags === 2 ? "#ccc" : "#333",
                        backgroundColor: "white",
                        "&:disabled": {
                          color: "#ccc",
                          borderColor: "#eee",
                          backgroundColor: "#f9f9f9",
                        },
                        "&:hover:not(:disabled)": {
                          backgroundColor: "transparent !important",
                          borderColor: "#333",
                        },
                      }}
                    >
                      <Add sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ px: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 13,
                      color: "#666",
                      textAlign: "left",
                      display: "block",
                    }}
                  >
                    Baggage per passenger
                  </Typography>
                </Box>
              </Paper>
              {/* Click outside to close */}
              {/* {Boolean(bagsAnchorEl) && ( // Removed as per new_code
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,

                    zIndex: 1300,
                  }}
                  onClick={() => setBagsAnchorEl(null)}
                />
              )} */}
            </Popper>
          </Box>
        )}

        {/* Adult Dropdown - only show in Multi-city */}
        {tripType === "Multi-city" && (
          <Box>
            <Button
              onClick={(e) => {
                closeAllDropdowns();
                setPassengersAnchorEl(e.currentTarget);
              }}
              endIcon={<KeyboardArrowDown />}
              sx={{
                color: "#333",
                fontWeight: 500,
                fontSize: "14px",
                textTransform: "none",
                minWidth: "auto",
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#000",
                },
              }}
            >
              {getTravelerLabel({
                adults,
                students,
                seniors,
                youths,
                childrenCount,
                toddlers,
                infants,
              })}
            </Button>
            <PassengerDropdown
              open={Boolean(passengersAnchorEl)}
              anchorEl={passengersAnchorEl}
              onClose={() => setPassengersAnchorEl(null)}
              counts={{
                adults,
                students,
                seniors,
                youths,
                childrenCount,
                toddlers,
                infants,
              }}
              setCounts={({
                adults: a,
                students: s,
                seniors: se,
                youths: y,
                childrenCount: c,
                toddlers: t,
                infants: i,
              }) => {
                setAdults(a);
                setStudents(s);
                setSeniors(se);
                setYouths(y);
                setChildrenCount(c);
                setToddlers(t);
                setInfants(i);
              }}
              showCabinClass={true}
              cabinClass={cabin}
              setCabinClass={setCabin}
            />
          </Box>
        )}
      </Box>

      {/* Main Search Interface */}
      {tripType === "Multi-city" ? (
        <Box>
          {/* Multi-city Flight Segments */}
          {flightSegments.map((segment, index) => (
            <FlightSegment
              key={segment.id}
              segment={segment}
              index={index}
              isActive={
                index === flightSegments.length - 1 &&
                !segment.from &&
                !segment.to
              }
              flightSegments={flightSegments}
              updateFlightSegment={updateFlightSegment}
              removeFlightSegment={removeFlightSegment}
              setDatePopoverAnchor={setDatePopoverAnchor}
              setActiveSegmentId={setActiveSegmentId}
              formatDate={formatDate}
            />
          ))}

          {/* Add Another Flight, Clear All, and Search Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Button
                onClick={addFlightSegment}
                disabled={flightSegments.length >= 6}
                sx={{
                  color: flightSegments.length >= 6 ? "#ccc" : "#333",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  ml: 2,
                  p: 0,
                  cursor:
                    flightSegments.length >= 6 ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration:
                      flightSegments.length >= 6 ? "none" : "underline",
                  },
                  "&:disabled": {
                    color: "#ccc",
                  },
                }}
              >
                Add another flight
              </Button>
              <Button
                onClick={clearAllSegments}
                sx={{
                  color: "#333",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  p: 0,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Clear all
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 1,
              }}
            >
              <Button
                onClick={() => { }}
                variant="contained"
                sx={{
                  background:
                    "linear-gradient(135deg, #ff690f 0%, #e8381b 100%)",
                  color: "white",
                  width: 50,
                  height: 50,
                  minWidth: 50,
                  mr: 7,
                  borderRadius: 3,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #e8381b 0%, #d6301a 100%)",
                  },
                }}
              >
                <Search sx={{ fontSize: 24 }} />
              </Button>

              {/* Direct flights only checkbox - below search button
              <FormControlLabel
                control={
                  <Checkbox
                    checked={directOnly}
                    onChange={(e) => setDirectOnly(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#999" }}
                  >
                    Direct flights only
                  </Typography>
                }
              /> */}
            </Box>
          </Box>
        </Box>
      ) : (
        /* Original Round-trip/One-way Interface */
        <>
          {/* Main Search Bar */}
          <Paper
            elevation={1}
            ref={searchBarRef}
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: 2,
              overflow: "visible",
              border: "1px solid #e0e0e0",
              backgroundColor: "white",
              position: "relative",
              zIndex: 1,

            }}
          >
            {/* From/To with floating swap icon */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                flex: 4, // Increased from 3 to 4 to make from/to bigger
                minWidth: 0,

              }}
            >
              {/* From Field */}
              <Box sx={{
                flex: 1, alignItems: "center", minWidth: 0, position: "relative", minHeight: "60px", "&:hover": {
                  backgroundColor: "#d9e2e8",
                  borderRadius: "8px",
                }, "& .MuiAutocomplete-inputRoot": {
                  padding: "16px !important",
                }, "&:focus-within": {
                  border: "1px solid #333",
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                }
              }}>
                <Autocomplete
                  options={groupedCities}
                  getOptionLabel={(option) =>
                    option
                      ? option.displayname ||
                      `${option.city}, ${option.country} ${option.code}`
                      : ""
                  }
                  PopperComponent={CustomPopper}
                  PaperComponent={({ children }) => (
                    <Paper
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        mt: 2,
                        mb: 2,
                        fontSize: "16px",
                        width: 520,
                        p: "16px"
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                  ListboxProps={{
                    id: "from-listbox",
                    sx: {
                      "& .MuiAutocomplete-option": {
                        p: "16px",
                        fontSize: "16px",
                        '&[aria-selected="true"]': {
                          backgroundColor: "#f3f5f6",
                        },
                        "&:hover": {
                          backgroundColor: "#e6ebef !important",
                          borderRadius: "8px",
                        },
                      },
                      // Hide scrollbar while keeping scrollable
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      "-ms-overflow-style": "none", // IE and Edge
                      "scrollbarWidth": "none", // Firefox
                    },
                  }}
                  slotProps={{
                    popper: {
                      placement: "bottom-start",
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: false,
                        },
                        {
                          name: "flip",
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  value={from.map(
                    (f) =>
                      groupedCities.find(
                        (c) => `${c.city} (${c.code})` === f
                      ) || null
                  )}
                  onChange={(_, newValue) => {
                    // Limit to 3 selections
                    if (newValue && newValue.length <= 3) {
                      const newFromValues = newValue
                        .filter((v) => v !== null) // Filter out null values
                        .map((v) => `${v.city} (${v.code})`);
                      const wasEmpty = from.length === 0;

                      setFrom(newFromValues);

                      // Auto-open To field only after FIRST selection (when from was empty)
                      if (wasEmpty && newFromValues.length === 1) {
                        setFromFieldOpen(false); // Close from dropdown
                        setTimeout(() => {
                          setToFieldOpen(true); // Open to dropdown
                        }, 300);
                      } else if (newFromValues.length > 1) {
                        // Close dropdown after any selection
                        setFromFieldOpen(false);
                        setTimeout(() => {
                          setToFieldOpen(true); // Open to dropdown
                        }, 300);
                      }
                    } else {
                      setFromFieldOpen(false);
                    }
                  }}
                  open={fromFieldOpen}
                  onOpen={() => {
                    closeAllDropdowns();
                    setFromFieldOpen(true);
                    resetListboxScrollToTop("from-listbox");
                  }}
                  onClose={() => setFromFieldOpen(false)}
                  renderOption={(props, option) => {
                    if (!option) return null; // Early return if option is null
                    const isSelected = from.some(
                      (f) => f === `${option.city} (${option.code})`
                    );
                    const isDisabled = !isSelected && from.length >= 3;
                    const isSubAirport = option.sub;

                    return (
                      <li
                        {...props}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: isSubAirport ? "8px 12px 8px 48px" : "12px",
                          borderBottom: "1px solid #f0f0f0",
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          backgroundColor: "#fff",
                          "&:hover": {
                            backgroundColor: "#f3f5f6 !important",
                            borderRadius: "8px",
                            border: "1px solid #333",
                            "& .MuiAutocomplete-option": {
                              p: 2,
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            marginRight: 12,
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {isSubAirport && (
                            <SubdirectoryArrowRightIcon
                              sx={{
                                fontSize: "28px",
                                fontWeight: 900,
                                color: "#333333",
                              }}
                            />
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f0f3f5", fill: "#5a6872", p: 2, borderRadius: 2 }}>
                            <img
                              src="/614.png"
                              alt="airplane"
                              style={{
                                width: "24px",
                                height: "24px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        </span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: isSubAirport ? 400 : 600,
                              fontSize: "16px",
                              color: "#333",
                            }}
                          >
                            {isSubAirport
                              ? option.name
                              : `${option.city}, ${option.region || option.country
                              }, ${option.country}`}{" "}
                            <span style={{ color: "#666", fontWeight: 400 }}>
                              {option.code}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#666",
                              marginTop: 2,
                            }}
                          >
                            {isSubAirport
                              ? `${option.city}, ${option.region || option.country
                              }, ${option.country}`
                              : option.isMetroOnly
                                ? "All airports"
                                : option.name}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            const optionValue = `${option.city} (${option.code})`;
                            if (isSelected) {
                              setFrom(from.filter((f) => f !== optionValue));
                            } else if (from.length < 3) {
                              setFrom([...from, optionValue]);
                            }
                          }}
                          style={{
                            width: "16px",
                            height: "16px",
                            marginLeft: 12,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            accentColor: "#1976d2",
                            backgroundColor: isSelected ? "#1976d2" : "#f5f5f5",
                            border: isSelected
                              ? "2px solid #1976d2"
                              : "2px solid #ddd",
                            borderRadius: "2px",
                          }}
                        />
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={from.length === 0 || (from.length >= 0 && from.length < 3 && fromFieldOpen) ? "From?" : ""}
                      variant="standard"
                      onClick={() => {
                        // Smooth scroll down to the search section
                        const searchSection = document.querySelector('[data-search-section]');
                        if (searchSection) {
                          searchSection.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        sx: {
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#333",
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          width: "100%",
                          minHeight: "40px", // Ensure consistent height
                          "& .MuiAutocomplete-inputRoot": {
                            paddingRight: "60px !important", // Reserve space for swap icon
                          },
                        },
                      }}
                      sx={{ width: "100%" }}
                    />
                  )}
                  renderTags={(value, getTagProps) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 0.5,
                        alignItems: "center",
                        maxWidth: "calc(100% - 80px)", // Reserve more space for swap icon
                        mr: 1, // Add margin right for spacing
                      }}
                    >
                      {value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option?.code}
                          label={
                            value.length === 1
                              ? option?.displayname ||
                              `${option?.city}, ${option?.region || option?.country
                              }, ${option?.country} (${option?.code})`
                              : option?.code
                          }
                          size="medium"
                          deleteIcon={
                            <svg
                              viewBox="0 0 200 200"
                              width={value.length === 1 ? "22px" : "12px"}
                              height={value.length === 1 ? "22px" : "12px"}
                              xmlns="http://www.w3.org/2000/svg"
                              className="c_neb-item-icon"
                              role="presentation"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                              />
                            </svg>
                          }
                          sx={{
                            backgroundColor: "#f0f3f5",
                            color: "#192024",
                            borderRadius: 1,
                            fontSize: "14px",
                            height: "24px",
                            marginRight: 0,
                            marginBottom: 0,
                            fontWeight: 400,
                            lineHeight: "20px",
                            border: "1px solid #d9e2e8",
                            padding: "4px 0px",
                            width: "100%",


                            "&:hover": {
                              border: "1px solid #333",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  multiple
                  limitTags={3}
                  size="small"
                  disableCloseOnSelect
                  popupIcon={null}
                  clearIcon={null}
                />
              </Box>
              {/* Floating Swap Button */}
              <IconButton
                onClick={() => {
                  closeDatePicker();
                  swapLocations();
                }}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                  background: "#fff",
                  boxShadow: "0 2px 8px #e0e0e0",
                  borderRadius: "8px",
                  width: 36,
                  height: 36,
                  border: "1px solid #e0e0e0",
                  color: "#333",
                  "&:hover": {
                    background: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #ccc",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <SwapHoriz />
              </IconButton>
              {/* To Field */}
              <Box sx={{
                flex: 1, minWidth: 0, ml: 2, position: "relative", minHeight: "60px", "&:hover": {
                  backgroundColor: "#d9e2e8",
                  borderRadius: "8px",
                }, "& .MuiAutocomplete-inputRoot": {
                  padding: "16px !important",
                }, "&:focus-within": {
                  border: "1px solid #333",
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                }
              }}>
                <Autocomplete
                  options={groupedCities}
                  getOptionLabel={(option) =>
                    option
                      ? option.displayname ||
                      `${option.city}, ${option.country} ${option.code}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    !!option &&
                    !!value &&
                    option.code === value.code &&
                    !!option.sub === !!value.sub
                  }
                  open={toFieldOpen}
                  onOpen={() => {
                    closeAllDropdowns();
                    setToFieldOpen(true);
                    resetListboxScrollToTop("to-listbox");
                  }}
                  onClose={() => setToFieldOpen(false)}
                  value={to.map(
                    (t) =>
                      groupedCities.find(
                        (c) => `${c.city} (${c.code})` === t
                      ) || null
                  )}
                  inputValue={toQuery}
                  filterOptions={(options, params) => {
                    const filtered = defaultFilter(options, params);
                    const currentInput = (params && typeof params.inputValue === 'string' ? params.inputValue : toQuery) || "";
                    const isEmptyInput = currentInput.trim() === "";
                    const curatedCodesArr = ["NYC", "DEL", "LAX", "LAS", "HNL"]; // curated set

                    // Don't show curated list if "Anywhere" is in the input
                    if (currentInput === "Anywhere") {
                      return [];
                    }

                    if (isEmptyInput && to.length === 0) {
                      // Show ONLY curated image items + CTA when empty
                      const curated = options.filter(
                        (o) => curatedCodesArr.includes(o.code) && !o.sub
                      );
                      return [...curated, { code: "ANY_CTA", city: "Anywhere CTA" }];
                    }
                    return filtered;
                  }}
                  onChange={(_, newValue) => {
                    // Handle "Anywhere" as text input, not as chip
                    if (toQuery === "Anywhere") {
                      setToFieldOpen(false);
                      setTimeout(() => {
                        setDatePopoverAnchor(searchBarRef.current);
                      }, 300);
                      return;
                    }

                    // Limit to 3 selections for regular options
                    if (newValue && newValue.length <= 3) {
                      const newToValues = newValue
                        .filter((v) => v !== null) // Filter out null values
                        .map((v) => `${v.city} (${v.code})`);
                      const wasEmpty = to.length === 0;

                      setTo(newToValues);
                      setToQuery(""); // Clear the input after selection

                      // Auto-open date picker only after FIRST selection (when to was empty)
                      if (wasEmpty && newToValues.length === 1) {
                        setToFieldOpen(false); // Close to dropdown
                        setTimeout(() => {
                          setDatePopoverAnchor(searchBarRef.current);
                        }, 300);
                      } else if (newToValues.length > 1) {
                        // Close dropdown after any selection
                        setToFieldOpen(false);
                        setTimeout(() => {
                          setDatePopoverAnchor(searchBarRef.current);
                        }, 300);
                      }
                    } else {
                      setToFieldOpen(false);
                    }
                  }}
                  renderOption={(props, option) => {
                    if (!option) return null; // Early return if option is null
                    if (option.code === "ANY_CTA") {
                      // Special CTA row
                      return (
                        <li
                          {...props}
                          onClick={(e) => {
                            e.preventDefault();
                            // Set "Anywhere" as text input instead of adding as chip
                            setToQuery("Anywhere");
                            setToFieldOpen(false);
                            setTimeout(
                              () => setDatePopoverAnchor(searchBarRef.current),
                              200
                            );
                          }}
                          style={{ listStyle: "none", width: "100%", p: "0px !important" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              backgroundColor: "transparent",
                              color: "#000",
                              justifyContent: "space-between",
                              transition: "background-color 0.2s ease",
                              '&:hover': {
                                backgroundColor: '#079',
                                color: "#fff !important",
                              },
                              width: "100%",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  backgroundColor: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <svg viewBox="0 0 200 200" width="32" height="32" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                  <path d="M150 70l20 30h-50c-13.24 0-13.221-20 0-20H60V20h60v20h50l-20 30zM30 35.007V180h20V20h-4.993C36.719 20 30 26.719 30 35.007z" fill="#666" />
                                </svg>
                              </Box>
                              <Box>
                                <Typography sx={{ fontWeight: 700, color: "inherit" }}>
                                  Can't decide where to go?
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: "inherit", opacity: 0.8 }}>
                                  Explore your options with anywhere search
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                <path d="M142.762 134.801l-11.523-9.603l14.749-17.699H30v-15h115.987L131.238 74.8l11.523-9.603l25 30c5.335 6.404.272 9.279-24.999 39.604z" fill="currentColor" />
                              </svg>
                            </Box>
                          </Box>
                        </li>
                      );
                    }
                    const isSelected = to.some(
                      (t) => t === `${option.city} (${option.code})`
                    );
                    const isDisabled = !isSelected && to.length >= 3;
                    const isSubAirport = option.sub;
                    const isEmptyInput = !toQuery || toQuery.trim() === "";
                    const curatedCodes = new Set(["NYC", "DEL", "LAX", "LAS", "HNL"]);
                    const isCurated = curatedCodes.has(option.code) && !isSubAirport;
                    // Show large image style only when input is empty and option is curated
                    const showImageRow = isCurated && isEmptyInput;

                    // Get the correct image URL for curated items
                    const getCuratedImage = (code) => {
                      const imageMap = {
                        "NYC": "https://content.r9cdn.net/rimg/dimg/8c/a9/d1b21b20-city-15830-16eb6a60801.jpg?width=128&height=128&xhint=1887&yhint=913&outputtype=JPEG&crop=true",
                        "DEL": "https://content.r9cdn.net/rimg/dimg/6d/77/0cc45283-city-32821-176ddb032b7.jpg?width=128&height=128&xhint=1769&yhint=1931&outputtype=JPEG&crop=true",
                        "LAX": "https://content.r9cdn.net/rimg/dimg/a6/ae/d1a4a522-city-16078-190266aa26e.jpg?width=128&height=128&xhint=1430&yhint=902&outputtype=JPEG&crop=true",
                        "LAS": "https://content.r9cdn.net/rimg/dimg/0a/9e/073516d2-city-35107-1656d23229c.jpg?width=128&height=128&xhint=2420&yhint=1089&outputtype=JPEG&crop=true",
                        "HNL": "https://content.r9cdn.net/rimg/dimg/73/93/010fa630-city-28070-15b0fbf9066.jpg?width=128&height=128&xhint=1898&yhint=1299&outputtype=JPEG&crop=true"
                      };
                      return imageMap[code] || "https://via.placeholder.com/56";
                    };

                    return (
                      <li
                        {...props}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: isSubAirport ? "8px 12px 8px 48px" : "12px",
                          borderBottom: "1px solid #f0f0f0",
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          backgroundColor: "#fff",
                        }}
                      >
                        <span
                          style={{
                            marginRight: 12,
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {isSubAirport && (
                            <SubdirectoryArrowRightIcon
                              sx={{
                                fontSize: "28px",
                                fontWeight: 900,
                                color: "#333333",
                              }}
                            />
                          )}
                          {showImageRow && to.length === 0 ? (
                            <img
                              src={getCuratedImage(option.code)}
                              alt="thumb"
                              style={{
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f0f3f5", fill: "#5a6872", p: 2, borderRadius: 2 }}>
                              <img
                                src="/614.png"
                                alt="airplane"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>

                          )}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: showImageRow ? 700 : (isSubAirport ? 400 : 600),
                              fontSize: "16px",
                              color: "#333",
                            }}
                          >
                            {isSubAirport
                              ? option.name
                              : `${option.city}, ${option.region || option.country
                              }, ${option.country}`}{" "}
                            <span style={{ color: "#666", fontWeight: 400 }}>
                              {option.code}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: showImageRow ? "13px" : "14px",
                              color: "#666",
                              marginTop: 2,
                            }}
                          >
                            {isSubAirport
                              ? `${option.city}, ${option.region || option.country
                              }, ${option.country}`
                              : option.isMetroOnly
                                ? "All airports"
                                : option.name}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            const optionValue = `${option.city} (${option.code})`;
                            if (isSelected) {
                              setTo(to.filter((t) => t !== optionValue));
                            } else if (to.length < 3) {
                              setTo([...to, optionValue]);
                            }
                          }}
                          style={{
                            width: "16px",
                            height: "16px",
                            marginLeft: 12,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            accentColor: "#1976d2",
                            backgroundColor: isSelected ? "#1976d2" : "#f5f5f5",
                            border: isSelected
                              ? "2px solid #1976d2"
                              : "2px solid #ddd",
                            borderRadius: "2px",
                          }}
                        />
                      </li>
                    );
                  }}
                  PopperComponent={CustomPopper}
                  PaperComponent={({ children }) => (
                    <Paper
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        mt: 2,
                        mb: 2,
                        width: 520,
                        p: "16px",
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                  ListboxProps={{
                    id: "to-listbox",
                    sx: {
                      "& .MuiAutocomplete-option": {
                        p: "16px",
                        fontSize: "16px",
                        borderBottom: "1px solid #f0f0f0",
                        '&[aria-selected="true"]': {
                          backgroundColor: "#f3f5f6",
                        },
                        "&:hover": {
                          backgroundColor: "#e6ebef !important",
                          borderRadius: "8px",
                        },
                      },
                      // Hide scrollbar while keeping scrollable
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      "-ms-overflow-style": "none", // IE and Edge
                      "scrollbarWidth": "none", // Firefox
                    },
                  }}
                  slotProps={{
                    popper: {
                      placement: "bottom-start",
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: false,
                        },
                        {
                          name: "flip",
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={to.length === 0 || (to.length >= 0 && to.length < 3 && toFieldOpen) ? "To?" : ""}
                      variant="standard"
                      onChange={(e) => setToQuery(e.target.value)}
                      onClick={() => {
                        // Smooth scroll down to the search section
                        const searchSection = document.querySelector('[data-search-section]');
                        if (searchSection) {
                          searchSection.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        sx: {
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "#333",
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          width: "100%",
                          minHeight: "40px", // Ensure consistent height
                          "& .MuiAutocomplete-inputRoot": {
                            paddingLeft: (to.length > 0 || toQuery === "Anywhere") ? "8px !important" : "60px !important",
                          },
                          "& .MuiInputBase-input": {
                            color: "#333",
                            fontSize: "16px",
                          },
                        },
                      }}
                      sx={{ width: "100%" }}
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    // Don't show chips when "Anywhere" is in the input
                    if (!value || value.length === 0 || toQuery === "Anywhere") return null;
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: 0.5,
                          alignItems: "center",
                          maxWidth: "calc(100% - 80px)", // Reserve more space for swap icon
                          ml: 1, // Add margin left for spacing (opposite of From field)
                        }}
                      >
                        {value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option?.code}
                            label={
                              value.length === 1
                                ? option?.displayname ||
                                `${option?.city}, ${option?.region || option?.country}, ${option?.country} (${option?.code})`
                                : option?.code
                            }
                            size="small"
                            deleteIcon={
                              <svg
                                viewBox="0 0 200 200"
                                width={value.length === 1 ? "22px" : "12px"}
                                height={value.length === 1 ? "22px" : "12px"}
                                xmlns="http://www.w3.org/2000/svg"
                                className="c_neb-item-icon"
                                role="presentation"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                                />
                              </svg>
                            }
                            sx={{
                              backgroundColor: "#f0f3f5",
                              color: "#333",
                              borderRadius: 1,
                              fontSize: "14px",
                              height: "24px",
                              marginRight: 0,
                              marginBottom: 0,
                              fontWeight: 400,
                              lineHeight: "20px",
                              border: "1px solid #d9e2e8",
                              padding: "4px 0px",
                              display: "inline-flex",
                              flexShrink: 0,
                              "&:hover": {
                                border: "1px solid #333",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                  multiple
                  limitTags={3}
                  size="small"
                  disableCloseOnSelect
                  popupIcon={null}
                  clearIcon={null}
                />
              </Box>
            </Box>
            {/* Date Fields */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 30, alignSelf: "center", p: 0 }}
            />
            <Box
              sx={{
                // Reduced from 1 to 0.8 to make date field smaller
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                position: "relative",
              }}
            >
              {/* Departure Button */}
              <Button
                onClick={(e) => {
                  // Don't close date picker if it's already open, just switch fields
                  if (!datePopoverAnchor) {
                    setDatePopoverAnchor(searchBarRef.current);
                  }
                  setActiveDateField("departure");
                }}
                onMouseEnter={() => { }} // Removed as per new_code
                onMouseLeave={() => { }} // Removed as per new_code
                sx={{
                  justifyContent: "flex-start",
                  color: "#333",
                  fontWeight: 500,
                  fontSize: "16px",
                  textTransform: "none",
                  minHeight: "60px",
                  borderRadius: 2,
                  backgroundColor:
                    activeDateField === "departure" ? "#e6ebef" : "transparent",
                  border: "1px solid transparent",
                  "&:hover": {
                    backgroundColor:
                      activeDateField === "departure" ? "#e6ebef" : "#d9e2e8",
                  },
                  "&:focus-within": {
                    border: "1px solid #333",
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb",
                  }
                }}
              >
                {weekendSelectedDate
                  ? formatDateWithFlexibility(
                    weekendSelectedDate,
                    departureExact,
                    true
                  )
                  : departureDate
                    ? formatDateWithFlexibility(departureDate, departureExact)
                    : "Departure"}
              </Button>

              {/* Horizontal dash between Departure and Return */}
              {tripType !== "One-way" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.5,
                    opacity: 1, // Removed hoveredButton logic
                    transition: "opacity 0.2s ease",
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: 500,
                    mt: -0.5,
                    minHeight: "60px",
                    borderRadius: 2,
                    border: "1px solid transparent",
                    "&:hover": {
                      backgroundColor: "#d9e2e8",
                    },

                  }}
                >
                  -
                </Box>
              )}

              {/* Return Button - only show for return trip */}
              {tripType !== "One-way" && (
                <Button
                  onClick={(e) => {
                    // Don't close date picker if it's already open, just switch fields
                    if (!datePopoverAnchor) {
                      setDatePopoverAnchor(searchBarRef.current);
                    }
                    setActiveDateField("return");
                  }}
                  onMouseEnter={() => { }} // Removed as per new_code
                  onMouseLeave={() => { }} // Removed as per new_code
                  sx={{
                    justifyContent: "flex-start",
                    color: "#333",
                    fontWeight: 500,
                    fontSize: "16px",
                    textTransform: "none",
                    // px: 0.5,
                    minHeight: "60px",
                    borderRadius: 2,
                    backgroundColor:
                      activeDateField === "return" ? "#e6ebef" : "transparent",
                    border: "1px solid transparent",
                    "&:hover": {
                      backgroundColor:
                        activeDateField === "return" ? "#e6ebef" : "#d9e2e8",
                    },
                    "&:focus-within": {
                      border: "1px solid #333",
                      borderRadius: "8px",
                      backgroundColor: "#f9fafb",
                    }
                  }}
                >
                  {isWeekendMode
                    ? `${formatWeekendLabel(
                      weekendDeparture
                    )} - ${formatWeekendLabel(weekendReturn)}`
                    : isMonthMode
                      ? `${monthDuration} nights`
                      : returnDate
                        ? formatDateWithFlexibility(returnDate, returnExact)
                        : "Return"}
                </Button>
              )}
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 30, alignSelf: "center", p: 0 }}
            />
            {/* Passengers */}
            <Box sx={{ position: "relative" }}>
              {" "}
              {/* Reduced from 1 to 0.7 to make passenger field smaller */}
              <Button
                ref={passengerButtonRef}
                onClick={(e) => {
                  closeAllDropdowns();
                  setPassengersAnchorEl(e.currentTarget);
                }}
                sx={{
                  color: "#333",
                  fontWeight: 500,
                  fontSize: "16px",
                  textTransform: "none",
                  px: 2,
                  py: 1,
                  pr: "65px",
                  minHeight: "60px",
                  borderRadius: 2,
                  border: "1px solid transparent",
                  "&:hover": {
                    backgroundColor: "#d9e2e8",
                  },
                  "&:focus-within": {
                    border: "1px solid #333",
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb",
                  }
                }}
              >
                {getPassengerDisplay()}, {cabin}
              </Button>
              <PassengerMenu
                anchorEl={passengersAnchorEl}
                open={Boolean(passengersAnchorEl)}
                onClose={() => setPassengersAnchorEl(null)}
                adults={adults}
                setAdults={setAdults}
                students={students}
                setStudents={setStudents}
                seniors={seniors}
                setSeniors={setSeniors}
                youths={youths}
                setYouths={setYouths}
                childrenCount={childrenCount}
                setChildrenCount={setChildrenCount}
                toddlers={toddlers}
                setToddlers={setToddlers}
                infants={infants}
                setInfants={setInfants}
                cabin={cabin}
                setCabin={setCabin}
                flightSegments={flightSegments}
                setFlightSegments={setFlightSegments}

              />
            </Box>
            {/* Search Button */}
            <Button
              onClick={(e) => {
                handleSubmit(e);
                navigate("/results");
              }}
              variant="contained"
              sx={{
                position: "absolute",
                right: "10px",
                background: "linear-gradient(135deg, #ff690f 0%, #e8381b 100%)",
                color: "#fff",
                borderRadius: "8px",
                padding: "8px 16px",
                width: "44px",
                minWidth: 44,
                minHeight: 44,
                boxShadow: "0 2px 8px #e0e0e0",
                "&:hover": {
                  background: "#e65c00",
                },
              }}
            >
              <Search sx={{ fontSize: 28 }} />
            </Button>
          </Paper>

          {/* Bottom Options Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                variant="body2"
                sx={{ color: "#666", fontSize: "14px" }}
              >
                Compare vs. KAYAK
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSouthwest}
                    onChange={(e) => setIncludeSouthwest(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#333" }}
                  >
                    Southwest
                  </Typography>
                }
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={directOnly}
                  onChange={(e) => setDirectOnly(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", color: "#333" }}
                >
                  Direct flights only
                </Typography>
              }
            />
          </Box>
        </>
      )}

      {/* DatePickerPopover - shared between all trip types */}
      <DatePickerPopover
        open={Boolean(datePopoverAnchor)}
        anchorEl={datePopoverAnchor}
        onClose={() => {
          setDatePopoverAnchor(null);
          setActiveSegmentId(null);
          setActiveDateField(null);
        }}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        dateTab={dateTab}
        setDateTab={setDateTab}
        departureExact={departureExact}
        setDepartureExact={setDepartureExact}
        returnExact={returnExact}
        setReturnExact={setReturnExact}
        tripType={tripType}
        departureDate={departureDate}
        setDepartureDate={setDepartureDate}
        returnDate={returnDate}
        setReturnDate={setReturnDate}
        handleDateSelect={handleDateSelect}
        isDateSelected={isDateSelected}
        isDateInRange={isDateInRange}
        generateCalendarDates={generateCalendarDates}
        generatePriceForDate={generatePriceForDate}
        getPriceColor={getPriceColor}
        getPriceBackgroundColor={getPriceBackgroundColor}
        formatDate={formatDate}
        showPrices={from.length > 0 && from[0] && to.length > 0 && to[0]}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        activeDateField={activeDateField}
        weekendSelectedDate={weekendSelectedDate}
        setWeekendSelectedDate={setWeekendSelectedDate}
        weekendDeparture={weekendDeparture}
        setWeekendDeparture={setWeekendDeparture}
        weekendReturn={weekendReturn}
        setWeekendReturn={setWeekendReturn}
        isWeekendMode={isWeekendMode}
        setIsWeekendMode={setIsWeekendMode}
        monthDuration={monthDuration}
        setMonthDuration={setMonthDuration}
        setIsMonthMode={setIsMonthMode}
        isMonthMode={isMonthMode}
      />
    </Box>
  );
}

export default FlightSearchBar;
